// Sounds and packs (lane W3), on the shared client (services/api/client.js).
// /sounds and /packs are optional endpoints: a server without them answers
// { comingSoon: true }, and the pages say "coming soon", never an error.
import { apiGet } from "./client.js";
import { toParams } from "../../kit/soundFilters.js";

/**
 * One page of the catalog (GET /sounds): { sounds, nextCursor, total }, or
 * { comingSoon: true }. `total` is null when the server didn't send one
 * (SI-13); only the first page carries it.
 */
export async function searchSounds(query, { cursor = null, limit = 30, signal = null } = {}) {
  const res = await apiGet("/sounds", toParams(query, { cursor, limit }), { optional: true, signal });
  if (res && res.comingSoon) return { comingSoon: true };
  const rows = res && Array.isArray(res.sounds) ? res.sounds : Array.isArray(res) ? res : [];
  const total = res && Number.isFinite(Number(res.total)) && res.total !== null ? Number(res.total) : null;
  return {
    sounds: rows,
    nextCursor: (res && typeof res.next_cursor === "string" && res.next_cursor) || null,
    total,
  };
}

/**
 * Every published pack (GET /packs?limit=100): a bare list, at most 100, as
 * list_packs_route returns it. Filters, sorting and search are the page's.
 */
export async function listPacks({ curator = null } = {}) {
  const res = await apiGet("/packs", { limit: 100, curator: curator || undefined }, { optional: true });
  if (res && res.comingSoon) return { comingSoon: true };
  return { packs: Array.isArray(res) ? res : [] };
}

/** One pack's page (GET /packs/<slug>), its sounds included. */
export function getPack(slug) {
  return apiGet(`/packs/${encodeURIComponent(slug)}`);
}

// "Or get the whole pack" when a row names no packs (B6 absent): the
// seller's published packs and their pages, shared by every sheet and kept
// for five minutes. A failed lookup is dropped so the next open tries again.
// GET /packs rows carry only a price summary, with no sound hashes, so the
// pages answer it.
const PACKS_TTL_MS = 5 * 60 * 1000;
const PACKS_CHECKED = 6;
const packsBySeller = new Map();

export function sellerPackPages(seller) {
  const hit = packsBySeller.get(seller);
  if (hit && Date.now() - hit.at < PACKS_TTL_MS) return hit.pages;
  const pages = listPacks({ curator: seller })
    .then((res) => {
      const slugs = (res.packs || [])
        .filter((p) => p && p.slug && (!p.state || p.state === "published"))
        .slice(0, PACKS_CHECKED)
        .map((p) => p.slug);
      return Promise.all(slugs.map((slug) => getPack(slug).catch(() => null)));
    })
    .catch((e) => {
      packsBySeller.delete(seller);
      throw e;
    });
  packsBySeller.set(seller, { at: Date.now(), pages });
  return pages;
}

/**
 * The published pack page a sound is in, and the viewer hasn't bought: from
 * the row's own `packs` (B6) when it names them, else the seller's packs.
 * Null when there is none.
 */
export async function packForSound({ hash, seller, packs = null }) {
  if (!hash) return null;
  const named = Array.isArray(packs) ? packs.filter((p) => p && p.slug) : [];
  const pages = named.length
    ? await Promise.all(named.slice(0, PACKS_CHECKED).map((p) => getPack(p.slug).catch(() => null)))
    : seller
    ? await sellerPackPages(seller)
    : [];
  return (
    pages.find(
      (pg) =>
        pg &&
        !pg.purchased &&
        (!pg.state || pg.state === "published") &&
        (pg.loops || []).some((l) => l && l.loop_hash === hash && !l.takedown)
    ) || null
  );
}

export default { searchSounds, listPacks, getPack, sellerPackPages, packForSound };
